import { Box, Button, Divider, Typography, CircularProgress } from '@mui/material'
import { toast } from 'react-toastify'
import { Dialog } from '~/components/Dialog'
import { ContractActivateBulkModel, useActivateBulkContractMutation } from '~/graphql/types'
import { DragAndDrop } from '../Form/DragAndDrop'
import { FormProvider, useForm } from 'react-hook-form'
import { Form } from '../Form/Form'
import { Input } from '../Form/Input'
import Papa from 'papaparse'
import { useState } from 'react'
import { IconCheck, IconX } from '@tabler/icons-react'

export type ContractDeactivationDialogProps = {
  isVisible: boolean
  toggleModal: () => void
}

export const MassContractActivationDialog: React.FC<ContractDeactivationDialogProps> = ({ isVisible, toggleModal }) => {
  const methods = useForm({
    mode: 'onChange',

  })
  const [resultList, setResultList] = useState<ContractActivateBulkModel | null>(null)

  const [activateContracts, { loading }] = useActivateBulkContractMutation({
    onCompleted(data) {
      setResultList(data.activateBulkContract)
    },
    onError() {
      toast.error('Erro ao ativar contratos.')
    },
  })

  const handleActivateContracts = async () => {
    const annotation = methods.getValues('annotation') || ''
    const idsFromText: string[] = annotation
      .split('\n')
      .map((id: string) => id.trim())
      .filter((id: string) => id.length > 0)

    const files = methods.getValues('energyBill') as File[] | undefined
    const idsFromFile: string[] = await (async () => {
      if (files && files.length > 0) {
        const text = await files[0].text()
        const parsed = Papa.parse<string[]>(text, { skipEmptyLines: true })
        return (parsed.data as string[][])
          .map(row => Array.isArray(row) ? row[0] : row)
          .map(id => (id ?? '').trim())
          .filter(id => id.length > 0)
      }
      return []
    })()

    const contractIds = Array.from(new Set([...idsFromText, ...idsFromFile]))

    activateContracts({
      variables: {
        contractIds
      }
    })
  }

  const handleReset = () => {
    setResultList(null)
    methods.reset()
  }

  const annotation = methods.watch('annotation') || ''
  const files = methods.watch('energyBill') as File[] | undefined
  const isActivateDisabled = !annotation.trim() && (!files || files.length === 0)

  return (
    <Dialog
      isVisible={isVisible}
      title='Ativação em massa de contratos'
      onClose={toggleModal}

    >
      <Divider />
      <Box sx={{ display: 'flex', flexFlow: 'column', gap: '2rem', padding: '2rem 0' }}>
        {!resultList ? (
          <>
            <Typography>Anexe um arquivo em CSV ou preencha o campo abaixo com o ID de cada contrato a ser ativado:</Typography>
            <FormProvider {...methods}>
              <Form>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                  <DragAndDrop
                    canResend
                    name='energyBill'
                    $variant='secondary'
                    disabled={false}
                    label='Anexar arquivo'
                    fileDescription='CSV'
                    fileName={undefined}
                    fileUrl={''}
                    $row={true}
                  />
                  <Input
                    multiline
                    minRows={5}
                    name='annotation'
                    placeholder='Insira os IDs dos contratos a serem ativados, um por linha.'
                  />
                </Box>
              </Form>
            </FormProvider>
            <Typography>Deseja alterar o status dos contratos informados para <strong>Ativo?</strong></Typography>
          </>
        )
          : (
            <>
              <Typography>Veja abaixo quais contratos foram ativados corretamente e quais falharam, com as respectivas justificativas.</Typography>
              <Box sx={{ maxHeight: 250, overflowY: 'auto', mb: 2 }}>
                {resultList.activated.map((id) => (
                  <Box key={id} sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: '24px', py: 1, borderBottom: '1px solid #eee' }}>
                    <Typography variant='body2' color='#999999'>{id}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography variant='body2'>Ativado</Typography>
                      <IconCheck color='#00A86B' />
                    </Box>
                    <Typography variant='body2' sx={{ flex: 1, textAlign: 'end' }}>-</Typography>
                  </Box>
                ))}
                {resultList.notActivated.map((item) => (
                  <Box key={item.id} sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: '24px', py: 1, borderBottom: '1px solid #eee' }}>
                    <Typography variant='body2' color='#999999'>{item.id}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography variant='body2'>Erro</Typography>
                      <IconX color='#EF4444' />
                    </Box>
                    <Typography variant='body2' sx={{ flex: 1, textAlign: 'end' }}>{item.error || '-'}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

      </Box>
      <Divider />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '2rem' }}>
        {!resultList ? (
          <>
            <Button disabled={loading} onClick={toggleModal} color='secondary'>Cancelar</Button>
            <Button
              endIcon={loading ? <CircularProgress size={24} color='inherit' /> : null}
              onClick={handleActivateContracts}
              disabled={loading || isActivateDisabled}
            >
              Ativar contratos
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleReset} color='secondary'>Ativar mais contratos</Button>
            <Button onClick={
              () => {
                toggleModal()
                handleReset()
              }} variant='contained'>Concluir</Button>
          </>
        )}
      </Box>
    </Dialog>
  )
}
